import * as dateFnsCompat from 'utils/date-fns-compat';

import fp from 'lodash/fp';
import PropTypes from 'prop-types';
import React from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { connect } from 'react-redux';
import {
  branch,
  compose,
  defaultProps,
  lifecycle,
  renderComponent,
  setPropTypes,
  withHandlers,
  withProps,
  withStateHandlers
} from 'recompose';
import { bindActionCreators } from 'redux';
import { CpdCategoryName, industryType } from '../../../../../constants';
import { exportTrainingPlanReportPdf } from '../../../../../reports/export-training-plan-report-pdf';
import {
  cpdPointsForCpdCategory,
  cpdPointsSum,
  fullName,
  getUserIndustryCpdCategorys,
  handleInvalidForm,
  responsibleManagerAdded
} from '../../../../../services';
import {
  currentUserIndustry,
  currentUserSelector,
  userActions
} from '../../../../../state/modules/user';
import { branchFuncs } from '../../../../../utils';
import {
  ConfirmationModal,
  FormSubmissionMessage,
  Icon
} from '../../../../components';
import Loading from '../../../loading';
import TrainingPlanEdit from './training-plan-edit';
import { filterNonLegacy } from '../../../../../common/cpd-category-filter';

const TrainingPlanLocked = () => (
  <Row id='training-plan-locked' className='no-gutter'>
    <Col xs={12} sm={2} smOffset={1}>
      <p key='heading'>
        <strong>
          Training plan locked{' '}
          <Icon icon='warning-large' className='icon-x2 icon-red' />
        </strong>
      </p>
    </Col>
    <Col xs={12} sm={8} smOffset={1} className='col-align'>
      <p>
        Your training plan is set by your manager and cannot be modified. To
        request changes to your training plan, please contact your team manager
        directly.
      </p>
    </Col>
  </Row>
);

const CpdSettings = ({
  user,
  showSubmissionMessage,
  submissionMessage,
  error,
  industry,
  resetFocusMessage,
  setCpdCategorys,
  closeSubmissionMessage,
  industryCpdCategorys,
  planLocked,
  headings: {
    cpdCategorys: cpdCategorysHeading,
    cpdDate: cpdDateHeading,
    cpdPoints: cpdPointsHeading,
    cpdAllocation: cpdAllocationHeading
  },
  planOwner,
  componentRef,
  rmSelected,
  setTrainingPlan,
  setIndustryRole,
  cpdCategorys,
  isUpdating,
  isSaved,
  hasFailed,
  trainingPlanUpdateConfirmation,
  showConfirmation,
  hideConfirmation,
  hideFailedBadge,
  profile,
  setProgressGoals,
  industriesWithCpdCategorys
}) => {
  const goals = fp.flow(fp.get('trainingPlans'), fp.flatMap('goals'))(profile);
  const defaultPlan = fp.flow(fp.get('trainingPlans'), fp.first)(profile);
  const userIndustryCpdCategorys = filterNonLegacy(
    getUserIndustryCpdCategorys(profile, industriesWithCpdCategorys)
  );
  const exportPdf = () => {
    exportTrainingPlanReportPdf({
      user,
      userIndustryCpdCategorys,
      cpdCategorys: fp.sortBy('cpdCategory.name')(
        fp.getOr([])('cpdCategorys')(defaultPlan)
      ),
      goals,
      fromDate: fp.getOr(Date.now())('fromDate')(defaultPlan),
      toDate: fp.getOr(Date.now())('toDate')(defaultPlan)
    });
  };
  return (
    <form
      noValidate
      ref={(form) => {
        if (componentRef) componentRef.formRef = form;
      }}
      onSubmit={(event) => {
        event.preventDefault();
        event.stopPropagation();
        setCpdCategorys(event, rmSelected);
      }}
    >
      <Row className='no-gutter'>
        <Col xs={12}>
          <Button className='right' onClick={exportPdf}>
            Export PDF
          </Button>
        </Col>
      </Row>
      {planLocked ? <TrainingPlanLocked /> : null}
      <FormSubmissionMessage
        {...{
          showSubmissionMessage,
          submissionMessage,
          error,
          stopFocusingMessage: resetFocusMessage,
          hideSubmissionMessage: closeSubmissionMessage
        }}
      />
      <Row className='no-gutter'>
        <Col xs={12}>
          <TrainingPlanEdit
            entityTrainingPlans={[
              {
                onSave: ({
                  entity,
                  trainingPlan,
                  industryId,
                  industryEntity,
                  goals,
                  callback
                }) => {
                  const trainingPlanWithEntityId = fp.map((plan) => ({
                    ...plan,
                    userId: entity.id
                  }))(trainingPlan);
                  if (
                    responsibleManagerAdded(
                      trainingPlanWithEntityId,
                      cpdCategorys,
                      industryCpdCategorys
                    )
                  ) {
                    showConfirmation({
                      trainingPlan: trainingPlanWithEntityId,
                      industryId,
                      industryEntity,
                      goals,
                      callback
                    });
                  } else {
                    setTrainingPlan({
                      industryId: industryId,
                      industry: industryEntity,
                      cpdCategorys: trainingPlanWithEntityId
                    });
                    setIndustryRole({ industry: industryEntity });
                    setProgressGoals(goals);
                    callback();
                  }
                },
                locked: planLocked,
                isUpdating,
                isSaved,
                hasFailed,
                entity: {
                  ...user,
                  name: fullName(user)
                },
                industryEntity: industry,
                goals,
                trainingPlan: fp.map(
                  ({
                    userId,
                    cpdCategoryId,
                    totalCpdPoints,
                    industryId,
                    maxMicroCpdPortion
                  }) => ({
                    userId,
                    cpdCategoryId,
                    totalCpdPoints,
                    industryId,
                    maxMicroCpdPortion
                  })
                )(cpdCategorys)
              }
            ]}
            entityTitle='My Training Plan'
            hideFailedBadge={hideFailedBadge}
            industryCpdCategorys={industryCpdCategorys}
          />
        </Col>
      </Row>

      <Row className='no-gutter submit-row text-center'>
        <Col xs={12} sm={7} smOffset={5}>
          <ConfirmationModal
            id='confirm-update-training-plan'
            show={trainingPlanUpdateConfirmation.show}
            actions={[
              {
                onConfirmAction: () => {
                  const {
                    trainingPlan,
                    industryId,
                    industryEntity,
                    goals,
                    callback
                  } = trainingPlanUpdateConfirmation.data;
                  setTrainingPlan({
                    industryId: industryId,
                    industry: industryEntity,
                    cpdCategorys: trainingPlan
                  });
                  setIndustryRole({
                    industry: industryEntity
                  });
                  setProgressGoals(goals);
                  callback();
                  hideConfirmation();
                },
                actionLabel: 'Yes'
              }
            ]}
            cancel={hideConfirmation}
            message={
              <span>
                You’re about to assign <strong>{planOwner}</strong> Responsible
                Manager content as a part of{' '}
                {planOwner === 'yourself' ? 'your' : 'their'} CPD training plan.
                This content should only be completed by RG105 representatives.
                Do you wish to continue?
              </span>
            }
            headingText={<span>Update Training Plan</span>}
          />
        </Col>
      </Row>
    </form>
  );
};

const mapStateToProps = (state) => ({
  isFetchingCpdCategorys: state.business.cpdCategorysIsFetching,
  isFetchingCpdCategory: state.business.isFetchingCpdCategory,
  currentIndustry: fp.first(currentUserIndustry(state)),
  user: currentUserSelector(state),
  profile: state.user.profile,
  industriesWithCpdCategorys: state.industry.industriesWithCpdCategorys
});
const mapDispatchToProps = (dispatch) => ({
  setIndustryRole: bindActionCreators(
    userActions.user.setIndustryRole,
    dispatch
  ),
  setTrainingPlan: bindActionCreators(
    userActions.user.setTrainingPlan,
    dispatch
  ),
  setProgressGoals: bindActionCreators(
    userActions.user.setProgressGoals,
    dispatch
  )
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  setPropTypes({
    cpdCategorys: PropTypes.array,
    cpdCategoryTransform: PropTypes.func,
    error: PropTypes.string,
    headings: PropTypes.object,
    userIndustryType: PropTypes.string,
    industry: PropTypes.object,
    industryCpdCategorys: PropTypes.array,
    isLoading: PropTypes.bool,
    isUpdating: PropTypes.bool,
    isSaved: PropTypes.bool,
    hasFailed: PropTypes.bool,
    saveCpdCategorys: PropTypes.func,
    planLocked: PropTypes.bool,
    hideFailedBadge: PropTypes.func
  }),
  defaultProps({
    cpdCategoryTransform: fp.identity
  }),
  branch(
    ({ isFetchingCpdCategorys, isFetchingCpdCategory, profile }) =>
      isFetchingCpdCategorys || isFetchingCpdCategory || !profile,
    renderComponent(Loading)
  ),
  withStateHandlers(
    {
      trainingPlanUpdateConfirmation: {
        show: false,
        data: undefined
      }
    },
    {
      showConfirmation: () => (data) => ({
        trainingPlanUpdateConfirmation: {
          show: true,
          data
        }
      }),
      hideConfirmation: () => () => ({
        trainingPlanUpdateConfirmation: {
          show: false,
          data: undefined
        }
      })
    }
  ),
  withStateHandlers(
    ({
      cpdCategorys,
      cpdCategoryTransform,
      industry,
      industryCpdCategorys
    }) => ({
      componentRef: {},
      focusMessage: false,
      industry,
      isFormUpdating: false,
      showSubmissionMessage: false,
      submissionMessage: '',
      validation: {},
      validationMessages: {},
      currentCpdCategorys: fp.map((cpdCategory) =>
        cpdCategoryTransform({
          name: fp.flow(
            fp.find(fp.flow(fp.get('id'), fp.eq(cpdCategory.cpdCategoryId))),
            fp.get('name')
          )(industryCpdCategorys),
          totalCpdPoints: 0,
          ...cpdCategory
        })
      )(cpdCategorys),
      showConfirmUpdate: false
    }),
    {
      toggleConfirmUpdate:
        ({ showConfirmUpdate }) =>
        () => ({
          showConfirmUpdate: !showConfirmUpdate
        }),
      onSelectCpdCategory:
        (_, { cpdCategoryTransform, industryCpdCategorys }) =>
        (cpdCategoryId, currentCpdCategorys) => ({
          currentCpdCategorys: branchFuncs(
            fp.some(fp.flow(fp.get('cpdCategoryId'), fp.eq(cpdCategoryId))),
            fp.remove(fp.flow(fp.get('cpdCategoryId'), fp.eq(cpdCategoryId))),
            fp.concat([
              cpdCategoryTransform({
                cpdCategoryId,
                name: fp.flow(
                  fp.find(fp.flow(fp.get('id'), fp.eq(cpdCategoryId))),
                  fp.get('name')
                )(industryCpdCategorys),
                totalCpdPoints: 0
              })
            ])
          )(currentCpdCategorys)
        }),
      resetFocusMessage: () => () => ({
        focusMessage: false
      }),
      closeSubmissionMessage: () => () => ({
        showSubmissionMessage: false
      }),
      onIndustryChange:
        ({ currentCpdCategorys }) =>
        (field, value, defaultValue, industry) => ({
          industry: {
            ...industry,
            [field]: parseInt(value, 10) || defaultValue
          },
          currentCpdCategorys:
            currentCpdCategorys.length === 1
              ? fp.map((cpdCategory) => ({
                  ...cpdCategory,
                  totalCpdPoints: parseInt(value, 10) || 0
                }))(currentCpdCategorys)
              : currentCpdCategorys
        }),
      onIndustryDateChange:
        ({ industry }) =>
        (cpdDate) => ({
          industry: {
            ...industry,
            cpdDay: dateFnsCompat.getDate(cpdDate),
            cpdMonth: dateFnsCompat.getMonth(cpdDate) + 1
          }
        }),
      onCpdCategorysChange: () => (event, currentCpdCategorys) => ({
        currentCpdCategorys: fp.map((cpdCategory) =>
          cpdCategory.cpdCategoryId === parseInt(event.name, 10)
            ? {
                ...cpdCategory,
                totalCpdPoints: parseInt(event.value, 10) || 0
              }
            : cpdCategory
        )(currentCpdCategorys)
      }),
      validateForm:
        ({ componentRef }, { currentIndustry }) =>
        (
          callback,
          currentCpdCategorys,
          industry,
          userIndustryType,
          confirmRMSelection
        ) => {
          const invalidCpdCategorys = fp.flow(
            fp.map(({ cpdCategoryId }) => [
              cpdCategoryId,
              cpdPointsForCpdCategory(cpdCategoryId)(currentCpdCategorys) > 0
            ]),
            fp.filter((x) => !x[1]),
            fp.fromPairs
          )(currentCpdCategorys);

          const validation =
            userIndustryType === industryType.Minimum
              ? {
                  totalCpdPoints:
                    industry.cpdPoints >= currentIndustry.minimumCpdPoints
                }
              : {
                  totalCpdPoints:
                    fp.isEmpty(currentCpdCategorys) || industry.cpdPoints > 0,
                  assignedCpdPoints:
                    fp.isEmpty(currentCpdCategorys) ||
                    industry.cpdPoints === cpdPointsSum(currentCpdCategorys),
                  ...invalidCpdCategorys
                };

          const validationMessages =
            userIndustryType === industryType.Minimum
              ? {
                  totalCpdPoints: validation.totalCpdPoints
                    ? null
                    : `You must complete a minimum of ${currentIndustry.minimumCpdPoints} CPD hours to be compliant with FASEA’s legislative CPD instruction.`
                }
              : {
                  totalCpdPoints: validation.totalCpdPoints
                    ? null
                    : 'At least one CPD hour must be undertaken.',
                  assignedCpdPoints: validation.assignedCpdPoints
                    ? null
                    : 'You must assign exactly this amount of CPD hours to the selected CPD categories.',
                  ...fp.mapValues(
                    () =>
                      'At least one CPD hour in each selected CPD category must be undertaken.'
                  )(invalidCpdCategorys)
                };

          const isValid = fp.all(fp.identity)(validation);

          if (isValid) {
            callback();
          } else {
            if (componentRef.formRef) {
              handleInvalidForm(componentRef.formRef);
            }
          }

          return {
            isFormUpdating: isValid && !confirmRMSelection,
            validation: validation,
            validationMessages: validationMessages
          };
        },
      onSetCpdCategorys: () => () => ({
        showSubmissionMessage: false,
        isFormUpdating: true,
        submissionMessage: 'Training plan successfully updated.'
      }),
      onUpdatingFinish: () => (updateFinished) => ({
        isFormUpdating: !updateFinished,
        focusMessage: updateFinished,
        showSubmissionMessage: updateFinished
      }),
      initComponentRef: () => (ref) => ({
        componentRef: ref
      })
    }
  ),
  withProps(({ currentCpdCategorys }) => ({
    rmSelected: fp.find(
      ({ name }) => name === CpdCategoryName.responsibleManager
    )(currentCpdCategorys)
  })),
  withHandlers({
    setCpdCategorys:
      ({
        onSetCpdCategorys,
        validateForm,
        saveCpdCategorys,
        currentCpdCategorys,
        industry,
        industryCpdCategorys,
        userIndustryType,
        user,
        toggleConfirmUpdate,
        showConfirmUpdate,
        rmSelected,
        currentIndustry
      }) =>
      (e, confirm) => {
        if (currentCpdCategorys.length === 1) {
          currentCpdCategorys = currentCpdCategorys.map((aoa) => ({
            ...aoa,
            totalCpdPoints: industry.cpdPoints
          }));
        }

        const mandatoryCpdCategorys = fp.flow(
          fp.filter(({ mandatory }) => mandatory),
          fp.map(({ id, minimumCpd }) => ({
            cpdCategoryId: id,
            totalCpdPoints: minimumCpd,
            userId: user.id
          }))
        )(industryCpdCategorys);

        const selectedCpdCategorys = fp.uniqBy(
          ({ cpdCategoryId }) => cpdCategoryId
        )(currentCpdCategorys.concat(mandatoryCpdCategorys));

        onSetCpdCategorys();
        validateForm(
          () => {
            if (rmSelected && confirm) {
              toggleConfirmUpdate();
            } else {
              saveCpdCategorys({
                cpdCategorys: selectedCpdCategorys,
                industry,
                industryId: currentIndustry.id,
                noRedirect: true
              });
            }
          },
          selectedCpdCategorys,
          industry,
          userIndustryType,
          rmSelected && confirm
        );
        handleInvalidForm(e.target);
      }
  }),
  lifecycle({
    componentWillReceiveProps({ isUpdating, showConfirmUpdate }) {
      const {
        isUpdating: wasUpdating,
        isFormUpdating,
        onUpdatingFinish,
        componentRef,
        initComponentRef
      } = this.props;
      const updateFinished = !isUpdating && wasUpdating && isFormUpdating;

      if (!componentRef) {
        initComponentRef(this);
      }

      if (updateFinished) {
        onUpdatingFinish(updateFinished);
      }
    }
  })
)(CpdSettings);



// WEBPACK FOOTER //
// ./src/containers/pages/app/account-settings/components/cpd-settings.js